import { useEffect, useRef, useCallback } from 'react';

interface UseBarcodeOptions {
  onBarcode: (value: string) => void;
  enabled?: boolean;
  setBarcodeDebugMessage: (message: string) => void;
}

export const useBarcodeScan = ({
  onBarcode,
  enabled = true,
  setBarcodeDebugMessage,
}: UseBarcodeOptions) => {
  const barcodeBufferRef = useRef<string>('');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetBuffer = useCallback(() => {
    barcodeBufferRef.current = '';
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) {
        return;
      }

      const isModifierKey = event.ctrlKey || event.metaKey || event.altKey;
      if (isModifierKey && event.key !== 'Enter') return;

      // Handle Enter key as barcode completion signal
      if (event.key === 'Enter') {
        if (barcodeBufferRef.current) {
          setBarcodeDebugMessage(`Barcode detected: ${barcodeBufferRef.current}`);
          onBarcode(barcodeBufferRef.current);
          resetBuffer();
        }
        return;
      }

      // Ignore special keys and function keys
      if (event.key.length > 1 && event.key !== 'Backspace') {
        if (event.key !== 'Backspace') return;
      }

      if (event.key !== 'Backspace' && event.key.length === 1) {
        barcodeBufferRef.current += event.key;
        setBarcodeDebugMessage(`keyboard character: ${event.key}`);
        // Removed timeout auto-completion - only trigger on Enter key
      } else if (event.key === 'Backspace') {
        barcodeBufferRef.current = barcodeBufferRef.current.slice(0, -1);
        setBarcodeDebugMessage(`keyboard: ${barcodeBufferRef.current}`);
      }
    },
    [enabled, onBarcode, resetBuffer, setBarcodeDebugMessage]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, handleKeyDown]);

  return { resetBuffer };
};
